import { doc, updateDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { db } from '../../../firebase';

export const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

export const ALLERGY_SEVERITIES = [
  { value: 'mild', label: 'Mild' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'severe', label: 'Severe' },
  { value: 'anaphylactic', label: 'Anaphylactic' },
];

const studentRef = (schoolId, studentId) =>
  doc(db, 'schools', schoolId, 'students', studentId);

const requireIds = (schoolId, studentId) => {
  if (!schoolId || !studentId) {
    throw new Error('healthProfileService: schoolId and studentId are required');
  }
};

const stamp = (user) => ({
  updatedAt: serverTimestamp(),
  updatedBy: user?.uid || null,
  updatedByName: user?.displayName || user?.email || null,
});

const newId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const updateHealthProfileFields = async ({
  schoolId, studentId, patch, user,
}) => {
  requireIds(schoolId, studentId);
  const stamps = stamp(user);
  const updates = { ...stamps };
  for (const [key, value] of Object.entries(patch)) {
    updates[`healthProfile.${key}`] = value;
    updates[`healthProfile.updatedAt`] = stamps.updatedAt;
    updates[`healthProfile.updatedBy`] = stamps.updatedBy;
    updates[`healthProfile.updatedByName`] = stamps.updatedByName;
  }
  await updateDoc(studentRef(schoolId, studentId), updates);
};

const overwriteList = async ({ schoolId, studentId, listKey, list, user }) => {
  requireIds(schoolId, studentId);
  const stamps = stamp(user);
  await updateDoc(studentRef(schoolId, studentId), {
    [`healthProfile.${listKey}`]: list,
    [`healthProfile.updatedAt`]: stamps.updatedAt,
    [`healthProfile.updatedBy`]: stamps.updatedBy,
    [`healthProfile.updatedByName`]: stamps.updatedByName,
  });
};

export const addProfileListItem = async ({
  schoolId, studentId, listKey, item, currentList, user,
}) => {
  const next = [...(currentList || []), { id: newId(), ...item }];
  await overwriteList({ schoolId, studentId, listKey, list: next, user });
  return next;
};

export const updateProfileListItem = async ({
  schoolId, studentId, listKey, itemId, patch, currentList, user,
}) => {
  const next = (currentList || []).map((it) =>
    it.id === itemId ? { ...it, ...patch } : it,
  );
  await overwriteList({ schoolId, studentId, listKey, list: next, user });
  return next;
};

export const removeProfileListItem = async ({
  schoolId, studentId, listKey, itemId, currentList, user,
}) => {
  const next = (currentList || []).filter((it) => it.id !== itemId);
  await overwriteList({ schoolId, studentId, listKey, list: next, user });
  return next;
};

export const clearHealthProfileField = async ({
  schoolId, studentId, key, user,
}) => {
  requireIds(schoolId, studentId);
  const stamps = stamp(user);
  await updateDoc(studentRef(schoolId, studentId), {
    [`healthProfile.${key}`]: deleteField(),
    [`healthProfile.updatedAt`]: stamps.updatedAt,
    [`healthProfile.updatedBy`]: stamps.updatedBy,
    [`healthProfile.updatedByName`]: stamps.updatedByName,
  });
};

export const hasCriticalAllergies = (profile) => {
  const list = profile?.allergies || [];
  return list.some((a) => a.severity === 'severe' || a.severity === 'anaphylactic');
};

export const summariseAllergies = (profile) => {
  const list = profile?.allergies || [];
  if (list.length === 0) return null;
  return list.map((a) => a.name).filter(Boolean).join(', ');
};

/**
 * Serious medical items worth surfacing prominently (e.g. on the student header)
 * so staff cannot miss them. "Serious" = severe/anaphylactic allergies, plus any
 * chronic condition on file (a chronic condition is listed because it matters —
 * asthma, diabetes, epilepsy). Returns a flat list of short, labelled tags.
 */
export const getSeriousMedicalItems = (profile) => {
  const items = [];

  for (const a of profile?.allergies || []) {
    if ((a.severity === 'severe' || a.severity === 'anaphylactic') && a.name) {
      items.push({ kind: 'allergy', label: a.name, severity: a.severity });
    }
  }

  for (const c of profile?.chronicConditions || []) {
    if (c.name) items.push({ kind: 'condition', label: c.name });
  }

  return items;
};
